import { useState } from "react"
import { Button, Checkbox, DatePicker, Flex, Radio, TimePicker } from "antd"
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import IconWithBg from "ui/IconWithBg"
import { Text } from "ui/Typography"
import { REPEAT_TYPES, WEEK_DAYS } from "../constants"
import { defaultTimeRow, buildSlots } from "../utils"
import styles from "./GenerateForm.module.css"
import { spacings } from "themes/variables"

const GenerateForm = ({ experienceName, durationMinutes, onGenerate }) => {
  const { t } = useTranslation()
  const [startDate, setStartDate] = useState(null)
  const [timeRows, setTimeRows] = useState([defaultTimeRow()])
  const [isRepeat, setIsRepeat] = useState(false)
  const [repeatType, setRepeatType] = useState(REPEAT_TYPES.WEEKDAYS)
  const [customDays, setCustomDays] = useState([])
  const [generateUntil, setGenerateUntil] = useState(null)

  const handleReset = () => {
    setStartDate(null)
    setTimeRows([defaultTimeRow()])
    setIsRepeat(false)
    setRepeatType(REPEAT_TYPES.WEEKDAYS)
    setCustomDays([])
    setGenerateUntil(null)
  }

  const handleTimeChange = (id, value) =>
    setTimeRows((prev) => prev.map((r) => (r.id === id ? { ...r, time: value } : r)))

  const handleDeleteTimeRow = (id) => {
    if (timeRows.length === 1) return
    setTimeRows((prev) => prev.filter((r) => r.id !== id))
  }

  const handleRepeatTypeChange = (value) => {
    setRepeatType(value)
    if (value !== REPEAT_TYPES.CUSTOM) setCustomDays([])
  }

  const handleToggleCustomDay = (dayIndex) => {
    const next = customDays.includes(dayIndex)
      ? customDays.filter((d) => d !== dayIndex)
      : [...customDays, dayIndex]
    setCustomDays(next)

    if (next.length === 7) {
      setRepeatType(REPEAT_TYPES.EVERYDAY)
      setCustomDays([])
      return
    }
    const isWeekdays = next.length === 5 && [1, 2, 3, 4, 5].every((d) => next.includes(d))
    const isWeekend = next.length === 2 && [0, 6].every((d) => next.includes(d))
    if (isWeekdays) { setRepeatType(REPEAT_TYPES.WEEKDAYS); setCustomDays([]) }
    else if (isWeekend) { setRepeatType(REPEAT_TYPES.WEEKEND); setCustomDays([]) }
  }

  const handleGenerate = () => {
    if (!startDate) return
    const validTimes = timeRows.filter((r) => r.time)
    if (validTimes.length === 0) return

    const endDate = isRepeat && generateUntil ? generateUntil : startDate
    const slots = buildSlots({ startDate, endDate, timeRows: validTimes, isRepeat, repeatType, customDays })

    onGenerate(slots)
  }

  const hasValidDays = !isRepeat || (repeatType !== REPEAT_TYPES.CUSTOM || customDays.length > 0)
  const canGenerate = !!startDate && timeRows.some((r) => r.time) && (!isRepeat || !!generateUntil) && hasValidDays

  return (
    <div className={styles.formPanel}>
      <Text size="lg" fw={600}>{t("experiences.ticketed_schedule.generate_section_title")}</Text>

      <Flex vertical gap={4} className="mt-m">
        <Text className={styles.fieldLabel}>{t("experiences.ticketed_schedule.experience_label")}</Text>
        <Flex align="center" className={styles.readonlyField}>
          <Text className={styles.readonlyText}>{experienceName}</Text>
        </Flex>
      </Flex>

      <Flex vertical className="mt-m">
        <Flex justify="flex-end">
          <Button type="link" size="small" className={styles.resetBtn} onClick={handleReset}>
            {t("experiences.ticketed_schedule.reset")}
          </Button>
        </Flex>
        <Flex vertical gap={4}>
          <Text className={styles.fieldLabel}>
            {t("experiences.ticketed_schedule.start_date_label")}
            <span className={styles.required}>*</span>
          </Text>
          <DatePicker
            value={startDate}
            onChange={setStartDate}
            disabledDate={(d) => d.isBefore(new Date(), "day")}
            format="MM/DD/YYYY"
            placeholder="MM/DD/YYYY"
            style={{ width: "100%" }}
            size="large"
          />
        </Flex>
      </Flex>

      <Flex vertical gap={4} className="mt-m">
        <Text className={styles.fieldLabel}>
          {t("experiences.ticketed_schedule.start_times_label")}
          <span className={styles.required}>*</span>
        </Text>
        <Flex vertical gap={4}>
          {timeRows.map((row) => {
            const endTime = row.time ? row.time.add(durationMinutes, "minute") : null
            return (
              <Flex key={row.id} align="center" gap={8}>
                <div className={styles.timepickerWrapper}>
                  <TimePicker
                    value={row.time}
                    onChange={(val) => handleTimeChange(row.id, val)}
                    use12Hours
                    format="h:mm A"
                    style={{ width: "100%" }}
                    size="large"
                  />
                </div>
                <Text className={styles.separator}>-</Text>
                <div className={`${styles.timepickerWrapper} ${styles.readonlyTimeField}`}>
                  <TimePicker
                    value={endTime}
                    use12Hours
                    format="h:mm A"
                    style={{ width: "100%" }}
                    size="large"
                    open={false}
                    disabled={false}
                    suffixIcon={null}
                    className={styles.endTimePicker}
                  />
                </div>
                <IconWithBg
                  icon={<DeleteOutlined />}
                  disabled={timeRows.length === 1}
                  className={styles.deleteRowBtn}
                  onClick={() => handleDeleteTimeRow(row.id)}
                />
              </Flex>
            )
          })}
        </Flex>
        <Button type="link" icon={<PlusOutlined />} className={styles.addTimeBtn} onClick={() => setTimeRows((prev) => [...prev, defaultTimeRow()])}>
          {t("experiences.ticketed_schedule.add_start_time")}
        </Button>
      </Flex>

      <div className={styles.divider} />

      <Flex vertical gap={16} className="mt-m">
        <Checkbox checked={isRepeat} onChange={(e) => setIsRepeat(e.target.checked)}>
          <Text className={styles.fieldLabel}>{t("experiences.ticketed_schedule.repeat_label")}</Text>
        </Checkbox>

        {isRepeat && (
          <Flex vertical gap={16}>
            <Radio.Group value={repeatType} onChange={(e) => handleRepeatTypeChange(e.target.value)}>
              <Flex gap={spacings.S} wrap="wrap">
                <Radio value={REPEAT_TYPES.EVERYDAY}>{t("experiences.ticketed_schedule.repeat_every_day")}</Radio>
                <Radio value={REPEAT_TYPES.WEEKDAYS}>{t("experiences.ticketed_schedule.repeat_weekdays")}</Radio>
                <Radio value={REPEAT_TYPES.WEEKEND}>{t("experiences.ticketed_schedule.repeat_weekend")}</Radio>
                <Radio value={REPEAT_TYPES.CUSTOM}>{t("experiences.ticketed_schedule.repeat_custom")}</Radio>
              </Flex>
            </Radio.Group>

            {repeatType === REPEAT_TYPES.CUSTOM && (
              <Flex gap={8} wrap="wrap">
                {WEEK_DAYS.map((day, index) => (
                  <button
                    key={day}
                    type="button"
                    className={`${styles.dayPill} ${customDays.includes(index) ? styles.dayPillActive : ""}`}
                    onClick={() => handleToggleCustomDay(index)}
                  >
                    {day}
                  </button>
                ))}
              </Flex>
            )}

            <Flex vertical gap={4}>
              <Text className={styles.fieldLabel}>
                {t("experiences.ticketed_schedule.generate_until_label")}
                <span className={styles.required}>*</span>
              </Text>
              <DatePicker
                value={generateUntil}
                onChange={setGenerateUntil}
                disabledDate={(d) => startDate && d.isBefore(startDate, "day")}
                format="MM/DD/YYYY"
                placeholder="MM/DD/YYYY"
                style={{ width: "100%" }}
                size="large"
              />
            </Flex>
          </Flex>
        )}

        <Button className="w-max-content" shape="round" color="primary" variant="outlined" size="large" disabled={!canGenerate} onClick={handleGenerate}>
          {t("experiences.ticketed_schedule.generate_button")}
        </Button>
      </Flex>
    </div>
  )
}

GenerateForm.propTypes = {
  experienceName: PropTypes.string.isRequired,
  durationMinutes: PropTypes.number.isRequired,
  onGenerate: PropTypes.func.isRequired
}

export default GenerateForm
